import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { MerchantSelector } from './MerchantSelector';

import { merchantApi } from '../lib/api/merchantApi';
import { CreateMerchantProfileRequest, Merchant } from '../lib/api/types';

// Validation schema for merchant profile creation
const createMerchantProfileSchema = z.object({
  ProfileName: z
    .string()
    .min(1, 'Profile name is required')
    .max(100, 'Profile name must be 100 characters or less'),
  PaymentProcessor: z
    .string()
    .min(1, 'Payment processor is required')
    .max(50, 'Payment processor must be 50 characters or less'),
  certStr: z
    .string()
    .min(1, 'Certificate string is required')
    .max(500, 'Certificate string must be 500 characters or less'),
  accountNum: z
    .string()
    .min(1, 'Account number is required')
    .max(50, 'Account number must be 50 characters or less'),
  termId: z
    .string()
    .min(1, 'Terminal ID is required')
    .max(50, 'Terminal ID must be 50 characters or less'),
  MerchantId: z
    .string()
    .min(1, 'Merchant selection is required'),
  AuthToken: z
    .string()
    .min(1, 'Auth token is required'),
});

type CreateMerchantProfileFormData = z.infer<typeof createMerchantProfileSchema>;

interface CreateMerchantProfileFormProps {
  onSuccess?: (profileId: number) => void;
}

export const CreateMerchantProfileForm: React.FC<CreateMerchantProfileFormProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedMerchant, setSelectedMerchant] = React.useState<Merchant | null>(null);

  const form = useForm<CreateMerchantProfileFormData>({
    resolver: zodResolver(createMerchantProfileSchema),
    defaultValues: {
      ProfileName: '',
      PaymentProcessor: 'ProPay',
      certStr: '',
      accountNum: '',
      termId: '',
      MerchantId: '',
      AuthToken: '',
    },
  });

  const createMerchantProfileMutation = useMutation({
    mutationFn: async (data: CreateMerchantProfileFormData) => {
      if (!selectedMerchant?.billerId) {
        throw new Error('Selected merchant is missing billerId');
      }
      const request: CreateMerchantProfileRequest = {
        ProfileName: data.ProfileName,
        PaymentProcessor: data.PaymentProcessor,
        ProcessorData: [
          {
            ProcessorField: 'certStr',
            Value: data.certStr,
          },
          {
            ProcessorField: 'accountNum',
            Value: data.accountNum,
          },
          {
            ProcessorField: 'termId',
            Value: data.termId,
          },
        ],
      };
      return merchantApi.createMerchantProfile(request, selectedMerchant.billerId, data.AuthToken);
    },
    onSuccess: (resp) => {
      toast.success('Merchant profile created successfully', {
        description: `Profile ID: ${resp.profileId}`,
      });
      form.reset();
      setSelectedMerchant(null);
      onSuccess?.(resp.profileId);
      // Refresh merchant profiles list
      queryClient.invalidateQueries({ queryKey: ['merchant-profiles'] });
      navigate('/merchant-profiles');
    },
    onError: (error: any) => {
      console.error('Error creating merchant profile:', error);
      let errorMessage = 'Failed to create merchant profile';

      if (error.response?.data?.ErrorMessage) {
        errorMessage = error.response.data.ErrorMessage;
      } else if (error.response?.data?.requestResult?.resultMessage) {
        errorMessage = error.response.data.requestResult.resultMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Failed to create merchant profile', {
        description: errorMessage,
      });
    },
  });

  const onSubmit = (data: CreateMerchantProfileFormData) => {
    if (!selectedMerchant) {
      toast.error('Please select a merchant');
      return;
    }

    createMerchantProfileMutation.mutate(data);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Merchant Profile</CardTitle>
        <CardDescription>
          Create a new merchant profile using merchant credentials
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Profile Name */}
            <FormField
              control={form.control}
              name="ProfileName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Profile Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter profile name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Processor */}
            <FormField
              control={form.control}
              name="PaymentProcessor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Processor</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter payment processor" {...field} />
                  </FormControl>
                  <FormDescription>
                    The payment processor for this profile (e.g., ProPay)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Certificate String */}
            <FormField
              control={form.control}
              name="certStr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Certificate String</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter certificate string" {...field} />
                  </FormControl>
                  <FormDescription>
                    ProPay certificate string for authentication
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Number */}
            <FormField
              control={form.control}
              name="accountNum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter account number" {...field} />
                  </FormControl>
                  <FormDescription>
                    ProPay account number
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Terminal ID */}
            <FormField
              control={form.control}
              name="termId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terminal ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter terminal ID" {...field} />
                  </FormControl>
                  <FormDescription>
                    ProPay terminal identifier
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Merchant Selection */}
            <FormField
              control={form.control}
              name="MerchantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Merchant</FormLabel>
                  <FormControl>
                    <MerchantSelector
                      selectedMerchantId={field.value}
                      onMerchantSelect={(merchant) => {
                        field.onChange(merchant?.id || '');
                        setSelectedMerchant(merchant);
                      }}
                      placeholder="Select a merchant"
                      required
                    />
                  </FormControl>
                  <FormDescription>
                    Select the merchant whose credentials will authorize this request
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Auth Token */}
            <FormField
              control={form.control}
              name="AuthToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Auth Token</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter merchant auth token" {...field} />
                  </FormControl>
                  <FormDescription>
                    Used with the merchant's billerId for Basic auth
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={createMerchantProfileMutation.isPending}>
              {createMerchantProfileMutation.isPending ? 'Creating...' : 'Create Merchant Profile'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
